'use client';

/**
 * CompareView — side-by-side comparison of two projects, shown while
 * store.compare is set. Generated strictly from the project/research
 * registries; closes on Escape or the close button.
 */

import { useEffect, useRef, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { useWorld } from '../lab/store';
import { lockScroll, unlockScroll } from '../lab/scrollLock';
import { projectBySlug, type Project } from '../content/projects';
import { researchBySlug } from '../content/research';
import styles from './CompareView.module.css';

function Column({ project }: { project: Project }) {
  return (
    <section className={styles.col} aria-label={project.name}>
      <header className={styles.colHead}>
        <h3 className={styles.name}>{project.name}</h3>
        <p className={styles.tagline}>{project.tagline}</p>
        <span className="tag tag--on">{project.statusLabel}</span>
      </header>
      <div className={styles.block}>
        <span className="label">Domains</span>
        <div className={styles.tags}>
          {project.domains.map((d) => (
            <span key={d} className="tag">
              {d}
            </span>
          ))}
        </div>
      </div>
      <div className={styles.block}>
        <span className="label">Summary</span>
        <p className={styles.summary}>{project.summary}</p>
      </div>
      <div className={styles.block}>
        <span className="label">Related research</span>
        <ul className={styles.researchList}>
          {project.research.map((slug) => {
            const r = researchBySlug.get(slug);
            return r ? (
              <li key={slug} className={styles.researchItem}>
                {r.title}
                <span className={styles.researchState}> · {r.stateLabel}</span>
              </li>
            ) : null;
          })}
        </ul>
      </div>
    </section>
  );
}

export default function CompareView() {
  const compare = useWorld((s) => s.compare);
  const setCompare = useWorld((s) => s.setCompare);
  const reducedMotion = useWorld((s) => s.reducedMotion);
  const overlayRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const lastActiveRef = useRef<HTMLElement | null>(null);

  // Focus capture + scroll lock + focus return, mirroring Ask Lab / the menu.
  useEffect(() => {
    if (!compare) return;
    lastActiveRef.current = document.activeElement as HTMLElement | null;
    lockScroll();
    const t = window.setTimeout(() => closeRef.current?.focus(), 30);
    return () => {
      window.clearTimeout(t);
      unlockScroll();
      // Restore focus to whatever opened the comparison. That is usually the
      // command palette, which closes as the comparison opens — so when the
      // stored element is gone, fall back to the persistent Ask Lab trigger
      // rather than dropping focus on <body>.
      const prev = lastActiveRef.current;
      if (prev && prev.isConnected) {
        prev.focus();
      } else {
        document
          .querySelector<HTMLElement>('[data-lab-ask-trigger]')
          ?.focus();
      }
    };
  }, [compare]);

  // Escape closes; Tab is contained within the dialog.
  const onKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      setCompare(null);
      return;
    }
    if (e.key === 'Tab') {
      const root = overlayRef.current;
      if (!root) return;
      const focusables = root.querySelectorAll<HTMLElement>(
        'button, [href], input, [tabindex]:not([tabindex="-1"])'
      );
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement;
      if (e.shiftKey && (active === first || !root.contains(active))) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (active === last || !root.contains(active))) {
        e.preventDefault();
        first.focus();
      }
    }
  };

  if (!compare) return null;
  const a = projectBySlug.get(compare.a);
  const b = projectBySlug.get(compare.b);
  if (!a || !b) return null;

  return (
    <div
      ref={overlayRef}
      className={`${styles.overlay} ${reducedMotion ? styles.noMotion : ''}`}
      role="dialog"
      aria-modal="true"
      aria-label={`Comparing ${a.name} and ${b.name}`}
      onKeyDown={onKeyDown}
    >
      <div className={styles.panel}>
        <header className={styles.head}>
          <span className="machine machine--sm">
            COMPARE · {a.slug} / {b.slug}
          </span>
          <button
            ref={closeRef}
            type="button"
            className={styles.close}
            onClick={() => setCompare(null)}
            aria-label="Close comparison"
          >
            esc · close
          </button>
        </header>
        <div className={styles.cols}>
          <Column project={a} />
          <div className={styles.divider} aria-hidden="true" />
          <Column project={b} />
        </div>
      </div>
    </div>
  );
}
